
import { useState } from "react";
import { useTheme } from "@mui/styles";
// import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, MenuItem, Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography } from "@mui/material";

import { Add, AddCircle, Cancel, CheckCircle, RemoveCircle, Save } from "@mui/icons-material";
import { RTFOPadSurveys } from "./RTFOPadSurveys";
import { RTFOPadPerfSchedule } from "./RTFOPadPerfSchedule";
import { RTFOPadParameters } from "./RTFOPadParameters";
import { RTFOSettingsHistory } from "./RTFOSettingsHistory";
import { rtfoConfig } from "./rtfoConfig";

import { setPadData } from "../../reducers/rtfoSlice";
import { RTFOPadMonitorSurveys } from "./RTFOPadMonitorSurveys";
import { useSocket } from "../../context/useSocket";

const padPhases = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'X',
  'Y',
  'Z'
];

export function RTFOSettings({ currentRTFO, darkMode, userEmail }) {

  const theme = useTheme();
  const dispatch = useDispatch();

  // const { enqueueSnackbar } = useSnackbar();
  const { socket } = useSocket();

  const [tabItem, setTabItem] = useState(0);
  const [name, setName] = useState(currentRTFO?.name || '');
  const [description, setDescription] = useState(currentRTFO?.description || '');
  const [padPhase, setPadPhase] = useState(currentRTFO?.padPhase || 'A');
  const [client, setClient] = useState(revertClientName(currentRTFO?.client) || 'Other');
  const [formation, setFormation] = useState(currentRTFO?.formation || 'Other');
  const [area, setArea] = useState(currentRTFO?.area || 'Other');
  const [units, setUnits] = useState('metric');
  const [monitorIds, setMonitorIds] = useState(currentRTFO?.monitorIds?.join(',') || '');
  const [monitorProvider, setMonitorProvider] = useState((currentRTFO?.monitorProvider?.charAt(0)?.toUpperCase()?.concat(currentRTFO?.monitorProvider?.substring(1, ))) || '-');
  const [seismicProvider, setSeismicProvider] = useState(currentRTFO?.seismicProvider?.charAt(0)?.toUpperCase()?.concat(currentRTFO?.seismicProvider?.substring(1, )) || '-');
  const [seismicProject, setSeismicProject] = useState(currentRTFO?.seismicProject || '');
  const [wells, setWells] = useState(currentRTFO?.perfSchedule ? Object.keys(currentRTFO?.perfSchedule?.wells)?.map(wellName => (
    {name: wellName, uwi: currentRTFO?.wellMapping?.[wellName] || '', lat: Number(currentRTFO?.surveys?.wells?.[wellName]?.surfaceLat), lon: Number(currentRTFO?.surveys?.wells?.[wellName]?.surfaceLon), kb: currentRTFO?.surveys?.wells?.[wellName]?.kb || 0, tvd: currentRTFO?.perfSchedule?.wells?.[wellName]?.tvd}
  )) : []);
  const [perfSchedule, setPerfSchedule] = useState(currentRTFO?.perfSchedule ? currentRTFO?.perfSchedule : { wells: {}});
  const [surveys, setSurveys] = useState(currentRTFO?.surveys ? currentRTFO?.surveys : { wells: {}});
  const [monitorSurveys, setMonitorSurveys] = useState(currentRTFO?.monitorSurveys ? currentRTFO?.monitorSurveys : {});
  const [dataProvider, setDataProvider] = useState(currentRTFO?.dataProvider ? (currentRTFO?.dataProvider === 'mrl' ? 'MRL' : currentRTFO?.dataProvider?.charAt(0)?.toUpperCase()?.concat(currentRTFO?.dataProvider?.substring(1, ))) : 'MRL');
  const [settings, setSettings] = useState(() => currentRTFO?.settings || {
    youngModulus: 5.2214e6,
    fracWidth: 0.15,
    t: 1.2,
    fracHeight: 30,
    fracAngle: 45,
    shadowRadius: 300,
    k: 0.001,
    phi: 0.03,
    cT: 8.12e-5,
    muW: 0.3545,
    bW: 1.007,
    poissonRatio: 0.2,
    wcc: 0.32,
    fluidEfficiency: 0.8,
    alpha: 40,
    wells: {}
  });
  const [addWellOpen, setAddWellOpen] = useState(false);

  return (
    <Paper 
      elevation={0} 
      sx={{boxShadow: 1}}
      style={{margin: '10px 20px 20px 20px', height: 'auto', overflow: 'auto', padding: 20, display: 'flex', flexDirection: 'column', justifyContent: 'center', backgroundColor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]}}
    >
      <div style={{display: 'flex', justifyContent: 'space-between'}}>
      <Typography variant="h5">Edit Pad</Typography>
      <Button startIcon={<Save />} onClick={handleSavePadClick} color="secondary" variant="contained" disabled={name?.length === 0 || wells?.length === 0}>
        Overwrite Settings
      </Button>
      </div>

      <div>

        <Tabs
          value={tabItem}
          onChange={(e, value) => value >= 0 && setTabItem(value)}
          style={{borderBottom: '1px solid lightgrey'}}
        >
          <Tab label="Settings" />
          <Tab label="Perf Schedules" disabled={!wells || wells?.length === 0} />
          <Tab label="Well Surveys" disabled={!wells || wells?.length === 0} />
          <Tab label="Monitor Surveys" disabled={!monitorIds || monitorIds?.length === 0} />
          <Tab label="Parameters" disabled={!wells || wells?.length === 0} />
          <Tab label="History" />
        </Tabs>

        <div style={{height: 'auto'}}>
          {
          tabItem === 0 ?
            <RTFOGeneralSettings 
              name={name} 
              setName={setName} 
              padPhase={padPhase} 
              setPadPhase={setPadPhase} 
              client={client} 
              setClient={setClient} 
              description={description} 
              setDescription={setDescription} 
              formation={formation} 
              setFormation={setFormation}
              area={area} 
              setArea={setArea}
              units={units}
              setUnits={setUnits}
              dataProvider={dataProvider}
              setDataProvider={setDataProvider}
              monitorProvider={monitorProvider}
              setMonitorProvider={setMonitorProvider}
              monitorIds={monitorIds}
              setMonitorIds={setMonitorIds}
              seismicProvider={seismicProvider}
              setSeismicProvider={setSeismicProvider}
              seismicProject={seismicProject}
              setSeismicProject={setSeismicProject}
              wells={wells}
              setWells={setWells}
              perfSchedule={perfSchedule}
              surveys={surveys}
              setAddWellOpen={setAddWellOpen}
            /> :
          tabItem === 1 ?
            <RTFOPadPerfSchedule wells={wells} perfSchedule={perfSchedule} setPerfSchedule={setPerfSchedule} />
            :
          tabItem === 2 ?
            <RTFOPadSurveys wells={wells} surveys={surveys} setSurveys={setSurveys} />
            :
          tabItem === 3 ?
            <RTFOPadMonitorSurveys monitorIds={monitorIds} monitorSurveys={monitorSurveys} setMonitorSurveys={setMonitorSurveys} />
            :
          tabItem === 4 ?
            <RTFOPadParameters wells={wells} settings={settings} setSettings={setSettings} />
            :
          tabItem === 5 ?
            <RTFOSettingsHistory currentRTFO={currentRTFO} />
            :
            <></>
          }
        </div>
        
        {addWellOpen && <AddWell setAddWellOpen={setAddWellOpen} wells={wells} setWells={setWells} setPerfSchedule={setPerfSchedule} setSurveys={setSurveys} setSettings={setSettings} />}


      </div>

    </Paper>
  );

          

  async function handleSavePadClick() {
    try {
      
      const wellMapping = {};

      wells?.forEach(w => (wellMapping[w.name] = w.uwi));

      const newPad = {
        _id: currentRTFO?._id,
        name,
        padPhase,
        description,
        formation,
        area,
        client: formatClientName(client),
        dataProvider: dataProvider?.toLowerCase(),
        createdBy: currentRTFO?.createdBy,
        createdOn: currentRTFO?.createdOn,
        lastModified: new Date()?.toLocaleString(),
        settings,
        stages: currentRTFO?.stages,
        perfSchedule,
        surveys,
        wellMapping,
        wellAlias: currentRTFO?.wellAlias || {},
        monitorIds: monitorIds?.length > 0 ? monitorIds?.split(',')?.map(el => el?.trim()) : [],
        monitorProvider: monitorProvider?.replace('-', '')?.toLowerCase(),
        fbas: currentRTFO?.fbas || {},
        isActive: currentRTFO?.isActive,
        seismicProject,
        seismicProvider: seismicProvider?.replace('-', '')?.toLowerCase(),
        seismicData: currentRTFO?.seismicData || {},
        monitorSurveys: monitorSurveys || currentRTFO?.monitorSurveys || {},
        fracSimCases: currentRTFO?.fracSimCases || {},
        monitorEvents: currentRTFO?.monitorEvents || [],
        fiberData: currentRTFO?.fiberData || [],
      };

      // Write change history
      const newChangeHistory = [...currentRTFO?.changeHistory || []];

      for (const parameter of Object.keys(currentRTFO?.settings)) {
        if (parameter !== 'wells' && Number(currentRTFO?.settings?.[parameter]) !== Number(settings?.[parameter])) {
          newChangeHistory.unshift({
            date: new Date()?.toLocaleString(),
            parameter,
            oldValue: Number(currentRTFO?.settings?.[parameter]),
            newValue: Number(settings?.[parameter]),
            changedBy: userEmail
          });
        };
      };


      newPad.changeHistory = newChangeHistory;

      socket?.send(JSON.stringify({
        messageType: 'savePad',
        pad: newPad,
      }));

      newPad.stageData = currentRTFO?.stageData;
      dispatch(setPadData(newPad));

      // const ENV = process.env.REACT_APP_NODE_ENV;
      // const BASE_URL_SSD = ENV === 'production' ? process.env.REACT_APP_BASE_URL_SSD : (ENV === 'staging' || ENV === 'development' ) ? process.env.REACT_APP_BASE_URL_SSD_STAGING : 'http://localhost:8080/api';

      // const options = {
      //   method: 'PUT',
      //   headers: {
      //     'x-api-key': process.env.REACT_APP_API_KEY,
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify({rtfo: newPad})
      // };
  
      // let result = await fetch(`${BASE_URL_SSD}/rtfo`, options);

      // if (result?.status === 200) {
      //   console.log('Pad updated successfully');
      //   enqueueSnackbar(`Pad ${newPad?.name} updated successfully`, { variant: 'success' });
      //   dispatch(setPadData({...newPad, stageData: currentRTFO?.stageData}));
      // };

      return;

      function formatClientName(clientName) {
        if (clientName === 'Whitecap') {
          return 'wcap';
        } else {
          return clientName?.split(' ')?.[0]?.toLowerCase();
        };
      };

    } catch (error) {
      console.log(error);
    };   
  };
};

function RTFOGeneralSettings({
  name, 
  setName, 
  padPhase, 
  setPadPhase, 
  client, 
  setClient, 
  description, 
  setDescription, 
  formation, 
  setFormation, 
  area, 
  setArea,
  units,
  setUnits,
  dataProvider,
  setDataProvider,
  monitorProvider, 
  setMonitorProvider,
  monitorIds,
  setMonitorIds,
  seismicProvider,
  setSeismicProvider,
  seismicProject,
  setSeismicProject,
  wells,
  setWells,
  perfSchedule,
  surveys,
  setAddWellOpen
}) {
  return (
    <div style={{display: 'flex', flexDirection: 'column', paddingBottom: 20}}>
      <div style={{marginTop: 30, marginBottom: 20}}>
        <Typography color='textSecondary'>GENERAL</Typography>
        <div style={{display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', marginTop: 5}}>

          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', width: '38%'}}>
            <Typography fontSize="14px">Pad Name</Typography>
            <TextField
              margin="dense"
              placeholder="Pad Name"
              type="text"
              size="small"
              value={name}
              variant="outlined"
              fullWidth
              disabled
              />
          </div>
          
          <div style={{width: '58%', display: 'flex', justifyContent: 'space-between'}}>

            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '35%'}}>
              <Typography fontSize="14px">Pad Phase</Typography>
              <TextField
                margin="dense"
                placeholder="Pad Phase"
                select
                size="small"
                value={padPhase}
                variant="outlined"
                fullWidth
                disabled
              >
                {padPhases.map((option) => (
                  <MenuItem key={option+'-key'} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </div>

            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '59%'}}>
              <Typography fontSize="14px">Client</Typography>
              <TextField
                margin="dense"
                size="small"
                select
                value={client}
                variant="outlined"
                fullWidth
                onChange={e => setClient(e.target.value)}
              >
                {[...rtfoConfig?.clients]?.sort().map((option) => (
                  <MenuItem key={option+'-key'} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          </div>
        </div>
        
        <div style={{display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', marginTop: 20}}>
          <div style={{display: 'flex', alignItems: 'flex-start', flexDirection: 'column', width: '100%'}}>
            <Typography fontSize="14px">Description</Typography>
            <TextField
              margin="dense"
              placeholder="Description"
              type="text"
              size="small"
              value={description}
              variant="outlined"
              fullWidth
              onChange={e => setDescription(e.target.value)}
            />
          </div>

        </div>

        <div style={{display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', marginTop: 20}}>
          
          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '38%'}}>
            <Typography fontSize="14px">Formation</Typography>
            <TextField
              margin="dense"
              size="small"
              select
              value={formation}
              variant="outlined"
              fullWidth
              onChange={e => setFormation(e.target.value)}
            >
              {[...rtfoConfig?.formations]?.map((option) => (
                <MenuItem key={option+'-key'} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </div>
          
          <div style={{width: '58%', display: 'flex', justifyContent: 'space-between'}}>
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '30%'}}>
              <Typography fontSize="14px">Area</Typography>
              <TextField
                margin="dense"
                size="small"
                select
                value={area}
                variant="outlined"
                placeholder="Area"
                fullWidth
                onChange={e => setArea(e.target.value)}
              >
                {[...rtfoConfig?.areas]?.sort()?.map((option) => (
                  <MenuItem key={option+'-key'} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </div>

            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '30%'}}>
              <Typography fontSize="14px">Data Provider</Typography>
              <TextField
                margin="dense"
                size="small"
                select
                value={dataProvider}
                variant="outlined"
                fullWidth
                onChange={e => setDataProvider(e.target.value)}
              >
                {rtfoConfig?.dataProviders?.map((option) => (
                  <MenuItem key={option+'-key'} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </div>

            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '30%'}}>
              <Typography fontSize="14px">Units</Typography>
              <TextField
                margin="dense"
                size="small"
                select
                value={units}
                variant="outlined"
                fullWidth
                onChange={e => setUnits(e.target.value)}
              >
                {['metric', 'field'].map((option) => (
                  <MenuItem key={option+'-key'} value={option}>
                    {option?.charAt(0)?.toUpperCase()?.concat(option?.substring(1, ))}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          </div>

        </div>
      </div>

      <div style={{marginTop: 30}}>
        <Typography color='textSecondary'>MONITOR WELLS</Typography>
        <div style={{display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', marginTop: 5}}>
          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '38%'}}>
            <Typography fontSize="14px">Monitor Provider</Typography>
            <TextField
              margin="dense"
              size="small"
              select
              value={monitorProvider}
              variant="outlined"
              fullWidth
              onChange={e => setMonitorProvider(e.target.value)}
            >
              {rtfoConfig?.monitorProviders?.map((option) => (
                <MenuItem key={option+'-key'} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </div>

          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '58%'}}>
            <Typography fontSize="14px">Monitor Well IDs</Typography>
            <TextField
              margin="dense"
              size="small"
              value={monitorIds}
              placeholder="mw01234, mw01235"
              variant="outlined"
              fullWidth
              onChange={e => setMonitorIds(e.target.value)}
              disabled={monitorProvider === '-'}
            />
          </div>
        </div>
      </div>

      <div style={{marginTop: 30}}>
        <Typography color='textSecondary'>SEISMIC DATA</Typography>
        <div style={{display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', marginTop: 5}}>
          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '38%'}}>
            <Typography fontSize="14px">Seismic Data Provider</Typography>
            <TextField
              margin="dense"
              size="small"
              select
              value={seismicProvider}
              variant="outlined"
              fullWidth
              onChange={e => setSeismicProvider(e.target.value)}
            >
              {rtfoConfig?.seismicProviders?.map((option) => (
                <MenuItem key={option+'-key'} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </div>

          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '58%'}}>
            <Typography fontSize="14px">Seismic Project ID</Typography>
            <TextField
              margin="dense"
              size="small"
              value={seismicProject}
              placeholder="Project ID"
              variant="outlined"
              fullWidth
              onChange={e => setSeismicProject(e.target.value)}
              disabled={seismicProvider === '-'}
            />
          </div>
        </div>
      </div>

      <div style={{marginTop: 30, height: 'auto'}}>
        <Typography color='textSecondary' style={{marginBottom: 5}}>WELLS</Typography>
        
        {
        wells?.length > 0 ? 
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{fontSize: 12}}>No.</TableCell>
                <TableCell sx={{fontSize: 12}}>Name</TableCell>
                <TableCell sx={{fontSize: 12}}>UWI</TableCell>
                <TableCell sx={{fontSize: 12}}>Latitude</TableCell>
                <TableCell sx={{fontSize: 12}}>Longitude</TableCell>
                <TableCell sx={{fontSize: 12}}>KB (m)</TableCell>
                <TableCell sx={{fontSize: 12}}>TVD (m)</TableCell>
                <TableCell sx={{fontSize: 12}}>Perf Schedule</TableCell>
                <TableCell sx={{fontSize: 12}}>Survey</TableCell>
                <TableCell sx={{fontSize: 12}}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {wells.map((well, index) => (
                <TableRow
                  key={well.uwi || index}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell sx={{fontSize: 12}}>{Number(index)+1}</TableCell>
                  <TableCell sx={{fontSize: 12}}>{well.name?.charAt(0)?.toUpperCase()?.concat(well.name?.substr(1, ))}</TableCell>
                  <TableCell sx={{fontSize: 12}}>{well.uwi}</TableCell>
                  <TableCell sx={{fontSize: 12}}>{well.lat}</TableCell>
                  <TableCell sx={{fontSize: 12}}>{well.lon}</TableCell>
                  <TableCell sx={{fontSize: 12}}>{well.kb}</TableCell>
                  <TableCell sx={{fontSize: 12}}>{well.tvd}</TableCell>
                  <TableCell sx={{fontSize: 12}}>{perfSchedule?.wells?.[well.name]?.stages && Object.keys(perfSchedule?.wells?.[well.name]?.stages)?.length > 0 ? <CheckCircle sx={{fontSize: 14}} style={{color: 'green'}} /> : <Cancel sx={{fontSize: 14}} style={{color: 'red'}} />}</TableCell>
                  <TableCell sx={{fontSize: 12}}>{surveys?.wells?.[well.name]?.surveyData?.length > 0 ? <CheckCircle sx={{fontSize: 14}} style={{color: 'green'}} /> : <Cancel sx={{fontSize: 14}} style={{color: 'red'}} />}</TableCell>
                  <TableCell sx={{fontSize: 12}}>
                    <div style={{display: 'flex'}}>
                      <IconButton onClick={() => handleRemoveWellClick(well.uwi)} title="Remove Well" ><RemoveCircle sx={{fontSize: 16}} /></IconButton>
                      {index === wells.length - 1 && <IconButton onClick={() => setAddWellOpen(true)} title="Add Well"><AddCircle sx={{fontSize: 16}} /></IconButton>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        :
        <div style={{width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <Button variant="outlined" onClick={() => setAddWellOpen(true)} startIcon={<Add />}>Add Wells</Button>
        </div>
        }
      </div>
    </div>
  );

  function handleRemoveWellClick(uwi) {
    setWells(prev => prev.filter(w => w.uwi !== uwi));
  };
};

function AddWell({ setAddWellOpen, wells, setWells, setPerfSchedule, setSurveys, setSettings }) {
  
  const [wellName, setWellName] = useState("");
  const [wellUWI, setWellUWI] = useState("");
  const [wellLat, setWellLat] = useState("");
  const [wellLon, setWellLon] = useState("");
  const [wellKb, setWellKb] = useState("");
  const [wellTVD, setWellTVD] = useState("");

  return (
  <Dialog 
    open={true}
    onClose={() => setAddWellOpen(false)}
    fullWidth={true}
    maxWidth="md"
  >
    <DialogTitle>Add New Well</DialogTitle>
    <DialogContent style={{display: 'flex', justifyContent: 'space-between'}}>

      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '15%', marginRight: 10}}>
        <Typography fontSize="14px">Name</Typography>
        <TextField
          error={wells.find(w => w.name === wellName) ? true : false}
          margin="dense"
          size="small"
          value={wellName}
          placeholder="Well Name"
          variant="outlined"
          fullWidth
          InputProps={{ style: { fontSize: 14 }}}
          onChange={e => setWellName(e.target.value)}
          helperText={wells.find(w => w.name === wellName) ? "Name already exists" : ""}
        />
      </div>

      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '25%', marginRight: 10}}>
        <Typography fontSize="14px">UWI</Typography>
        <TextField
          error={wells.find(w => w.uwi === wellUWI) ? true : false}
          margin="dense"
          size="small"
          value={wellUWI}
          placeholder="UWI"
          variant="outlined"
          fullWidth
          InputProps={{ style: { fontSize: 14 }}}
          onChange={e => setWellUWI(e.target.value)}
          helperText={wells.find(w => w.uwi === wellUWI) ? "UWI already exists" : ""}
        />
      </div>

      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '12.5%', marginRight: 10}}>
        <Typography fontSize="14px">Latitude</Typography>
        <TextField
          margin="dense"
          size="small"
          value={wellLat}
          placeholder="Latitude"
          variant="outlined"
          fullWidth
          InputProps={{ style: { fontSize: 14 }}}
          onChange={e => setWellLat(e.target.value)}
        />
      </div>

      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '12.5%', marginRight: 10}}>
        <Typography fontSize="14px">Longitude</Typography>
        <TextField
          margin="dense"
          size="small"
          value={wellLon}
          placeholder="Longitude"
          variant="outlined"
          fullWidth
          InputProps={{ style: { fontSize: 14 }}}
          onChange={e => setWellLon(e.target.value)}
        />
      </div>

      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '15%', marginRight: 10}}>
        <Typography fontSize="14px">KB</Typography>
        <TextField
          margin="dense"
          size="small"
          value={wellKb}
          placeholder="KB"
          variant="outlined"
          fullWidth
          onChange={e => setWellKb(e.target.value)}
          InputProps={{
            endAdornment: <InputAdornment position="end">m</InputAdornment>,
            style: { fontSize: 14 }
          }}
        />
      </div>

      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '15%'}}>
        <Typography fontSize="14px">TVD</Typography>
        <TextField
          margin="dense"
          size="small"
          value={wellTVD}
          placeholder="TVD"
          variant="outlined"
          fullWidth
          onChange={e => setWellTVD(e.target.value)}
          InputProps={{
            endAdornment: <InputAdornment position="end">m</InputAdornment>,
            style: { fontSize: 14 }
          }}
        />
      </div>

    </DialogContent>
    <DialogActions style={{display: 'flex', justifyContent: 'space-between'}}>
      <Button onClick={() => setAddWellOpen(false)} color="primary" style={{marginRight: 10}}>
        Cancel
      </Button>
      <Button onClick={handleAddWellClick} color="primary" variant="contained" disabled={wellName.length === 0 || wellUWI.length === 0 || wellLat.length === 0 || wellLon.length === 0 || wellKb.length === 0}>
        Add
      </Button>
    </DialogActions>
  </Dialog>
  );

  function handleAddWellClick() {
    setWells(prev => [...prev, {name: wellName?.toLowerCase(), uwi: wellUWI, lat: Number(wellLat), lon: Number(wellLon), kb: Number(wellKb), tvd: Number(wellTVD)}]);
    setSurveys(prev => {
      const newSurveys = JSON.parse(JSON.stringify(prev));
      newSurveys.wells[wellName?.toLowerCase()] = { surfaceLat: Number(wellLat), surfaceLon: Number(wellLon), surveyData: [] };
      return newSurveys;
    });
    setPerfSchedule(prev => {
      const newPerfSchedule = JSON.parse(JSON.stringify(prev));
      newPerfSchedule.wells[wellName?.toLowerCase()] = { stages: {} };
      return newPerfSchedule;
    });
    setSettings(prev => {
      const newSettings = JSON.parse(JSON.stringify(prev));
      newSettings.wells[wellName?.toLowerCase()] = { 
        casing1: { od: 139, id: 0, md: 3096, fc: 0.033, plExp: 1.7 }, 
        casing2: { od: 114, id: 0, md: 0, fc: 0.085, plExp: 1.701 }
      };
      return newSettings;
    })
    setAddWellOpen(false);
  };
};

function revertClientName(client) {
  switch(client) {
    case 'wcap':
      return 'Whitecap';
    case 'yangarra':
      return 'Yangarra';
    case 'artis':
      return 'Artis Exploration';
    case 'yoho':
      return 'Yoho Resources';
    case 'kiwetinohk':
      return 'Kiwetinohk';
    case 'empire':
      return 'Empire Energy';
    case 'vesta':
      return 'Vesta Energy';
    case 'baytex':
      return 'Baytex Energy';
    case 'ovintiv':
      return 'Ovintiv';
    case 'vermilion':
      return 'Vermilion Energy';
    case 'other':
      return 'Other';
    default:
      return client;
  };
};

// function handleArchivePadClick() {
//   try {
//     socket?.readyState === 1 &&
//     socket?.send(JSON.stringify({
//       messageType: 'archivePadData',
//       padName: currentRTFO?.name
//     }));
//   } catch (error) {
//     console.log(error);
//     return;
//   };
// };

// function handleConsolidatePadClick() {
//   try {
//     socket?.readyState === 1 &&
//     socket?.send(JSON.stringify({
//       messageType: 'consolidatePadData',
//       padName: currentRTFO?.name
//     }));
//   } catch (error) {
//     console.log(error);
//     return;
//   };
// };

// function handleClonePadClick() {
//   try {
//     socket?.readyState === 1 &&
//     socket?.send(JSON.stringify({
//       messageType: 'clonePad',
//       padName: currentRTFO?.name
//     }));
//   } catch (error) {
//     console.log(error);
//     return;
//   };
// };